import { Controller, useFormContext } from 'react-hook-form';
import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormLabel,
    InputLabel,
    MenuItem,
    Select,
    TextField
} from '@mui/material';

import { BoxType, GroupType, ScanTypeStatus } from '../../services/scan-types/schemas';
import { ScanTypeDetailsFormData } from './ScanTypeDetails';

type GeneralInformationFormProps = {
    isEnabled: boolean;
    canEditField: { isProtected: boolean };
};

export const GeneralInformationForm = ({ isEnabled, canEditField }: GeneralInformationFormProps) => {
    const form = useFormContext<ScanTypeDetailsFormData>();

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
            <TextField
                label="Name"
                InputLabelProps={{ shrink: true }}
                sx={{ width: '25rem' }}
                {...form.register('name')}
                disabled={!isEnabled}
                error={!!form.formState.errors.name?.message}
                helperText={form.formState.errors.name?.message}
                autoComplete="off"
            />

            <Controller
                control={form.control}
                name="groupType"
                render={({ field }) => (
                    <FormControl>
                        <InputLabel id={field.name}>Category</InputLabel>

                        <Select
                            {...field}
                            labelId={field.name}
                            label="Category"
                            sx={{ width: '25rem' }}
                            disabled={!isEnabled}
                        >
                            <MenuItem value={GroupType.AD_HOC_BOX}>Ad hoc box</MenuItem>
                            <MenuItem value={GroupType.INBOUND}>Inbound</MenuItem>
                            <MenuItem value={GroupType.OUTBOUND}>Outbound</MenuItem>
                        </Select>
                    </FormControl>
                )}
            />

            <Controller
                control={form.control}
                name="status"
                render={({ field }) => (
                    <FormControl>
                        <InputLabel id={field.name}>Status</InputLabel>

                        <Select
                            {...field}
                            labelId={field.name}
                            label="Status"
                            sx={{ width: '25rem' }}
                            disabled={!isEnabled}
                        >
                            <MenuItem value={ScanTypeStatus.ACTIVE}>Active</MenuItem>
                            <MenuItem value={ScanTypeStatus.DISABLED}>Disabled</MenuItem>
                            <MenuItem value={ScanTypeStatus.DRAFT}>Draft</MenuItem>
                        </Select>
                    </FormControl>
                )}
            />

            <Controller
                control={form.control}
                name="boxType"
                render={({ field }) => (
                    <FormControl>
                        <InputLabel id={field.name}>Box type</InputLabel>

                        <Select
                            {...field}
                            labelId={field.name}
                            label="Box type"
                            sx={{ width: '25rem' }}
                            disabled={!isEnabled}
                        >
                            <MenuItem value={BoxType.NORMAL}>Normal</MenuItem>
                            <MenuItem value={BoxType.IMPORT_ENABLED}>Import enabled</MenuItem>
                            <MenuItem value={BoxType.NO_BOXES}>No boxes</MenuItem>
                        </Select>
                    </FormControl>
                )}
            />

            <FormControl component="fieldset" variant="standard">
                <FormLabel component="legend">Settings</FormLabel>

                <FormGroup>
                    <Controller
                        control={form.control}
                        name="allowFailedChecks"
                        render={({ field }) => (
                            <FormControlLabel
                                label="Allow failed loggers"
                                control={<Checkbox {...field} checked={field.value} disabled={!isEnabled} />}
                                sx={{ width: 'max-content' }}
                            />
                        )}
                    />

                    <Controller
                        control={form.control}
                        name="isProtected"
                        render={({ field }) => (
                            <FormControlLabel
                                label="Is protected"
                                control={
                                    <Checkbox
                                        {...field}
                                        checked={field.value}
                                        disabled={!isEnabled || !canEditField.isProtected}
                                    />
                                }
                                sx={{ width: 'max-content' }}
                            />
                        )}
                    />
                </FormGroup>
            </FormControl>

            <TextField
                label="Description"
                InputLabelProps={{ shrink: true }}
                sx={{ width: '25rem' }}
                {...form.register('description')}
                multiline
                minRows={8}
                disabled={!isEnabled}
                error={!!form.formState.errors.description?.message}
                helperText={form.formState.errors.description?.message}
            />

            <TextField
                label="Created by"
                InputLabelProps={{ shrink: true }}
                sx={{ width: '25rem' }}
                {...form.register('createdByUsername')}
                disabled
            />

            <TextField
                label="Created at"
                InputLabelProps={{ shrink: true }}
                sx={{ width: '25rem' }}
                type="datetime-local"
                {...form.register('createdTime')}
                disabled
            />
        </Box>
    );
};
